import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { onPeopleChanged, roleLabel, sharedRealms } from '../account/sharing.ts';
import { realmKey, type OpenedRealm } from '../db/protocol.ts';
import { useLeaseView, type LeaseView } from './LeaseKeeper.tsx';

/** Players' or Readers' copy. */
export function copyName(role: string): string {
  return `${roleLabel(role)}s’ copy`;
}

interface Shown {
  readonly label: string;
  readonly detail: string;
  readonly explain: string;
}

/** While someone else edits, or you've stepped away: why it's read-only. Null while it's yours to edit. */
function waiting(lease: LeaseView | null): string | null {
  if (lease?.kind === 'held') return `read-only while ${lease.email} edits`;
  if (lease?.kind === 'away') return 'read-only: you stepped away';
  return null;
}

/** What the app bar says about the open realm, or null when it's this account's own and shared with no one. */
export function roleShown(opened: Pick<OpenedRealm, 'shared'>, people: number | null, lease: LeaseView | null = null): Shown | null {
  const { shared } = opened;
  if (shared?.previewOf) {
    return {
      label: `Preview: ${copyName(shared.role)}`,
      detail: 'nothing sent',
      explain: `What ${roleLabel(shared.role).toLowerCase()}s would get if you updated their copy now. Only you see it, and it isn’t kept.`,
    };
  }
  if (shared?.role === 'editor') {
    return {
      label: 'Co-owner',
      detail: waiting(lease) ?? (lease?.kind === 'mine' ? 'editing' : `shared by ${shared.ownerEmail}`),
      explain: `Co-owners see and change everything, taking turns: one person edits at a time. ${shared.ownerEmail} shares it, and only they can delete it.`,
    };
  }
  if (shared) {
    return {
      label: roleLabel(shared.role),
      detail: `read-only · shared by ${shared.ownerEmail}`,
      explain: `${roleLabel(shared.role)}s see what the realm shows ${roleLabel(shared.role).toLowerCase()}s. ${shared.ownerEmail} sends updates.`,
    };
  }
  if (!people) return null;
  return {
    label: 'Owner',
    detail: waiting(lease) ?? `shared with ${people} ${people === 1 ? 'person' : 'people'}`,
    explain: 'You can change everything, and you decide who it’s shared with and when they get your changes.',
  };
}

/**
 * The role the open realm is seen in, on the app bar (docs/SHARING-CO-OWNERS-PLAN.md
 * section 2.1). Pointing at it, or pressing it, says what the role can do.
 */
export function RealmRole() {
  const opened = useRouterState({
    select: (s) => s.matches.find((m) => m.routeId === '/_app/realm/$realmKey')?.loaderData as OpenedRealm | undefined,
  });
  const path = useRouterState({ select: (s) => s.location.pathname });
  const own = opened && !opened.shared ? realmKey(opened.realm.file) : null;
  // How many people the realm is shared with: asked again on each page, and when someone is invited or removed.
  const [people, setPeople] = useState<{ realm: string; n: number } | null>(null);
  useEffect(() => {
    if (!own) return;
    let live = true;
    const ask = () =>
      void sharedRealms().then(
        (counts) => live && setPeople({ realm: own, n: counts.get(own) ?? 0 }),
        () => undefined, // offline: nothing said
      );
    ask();
    const stop = onPeopleChanged(ask);
    return () => {
      live = false;
      stop();
    };
  }, [own, path]);
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [path]);

  const lease = useLeaseView(opened?.realm.file ?? null);
  const shown = opened ? roleShown(opened, people?.realm === own ? people.n : null, lease) : null;
  return (
    <span className="app-bar-role" aria-live="polite">
      {shown && (
        <>
          <button type="button" className="app-bar-role-chip" aria-expanded={open} title={shown.explain} onClick={() => setOpen((o) => !o)}>
            <strong>{shown.label}</strong> · {shown.detail}
          </button>
          {open && (
            <span className="app-bar-role-note" role="note">
              {shown.explain}
            </span>
          )}
        </>
      )}
    </span>
  );
}
